/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { FieldArray, FormikProvider, useFormik } from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import { useReportAttachmentDeleteByIdMutation, useReportAttachmentSearchMutation, useReportGetByIdMutation, useReportTicketAttachmentDeleteByIdMutation, useReportTicketAttachmentSearchMutation, useReportUpdateByIdMutation } from '../Report.api'
import { IReportDto, IReportFilter, IReportStatus, IReportUpdateRequestDto, IReportWorkOrderRequestDto, IWorkOrderLineType } from '../Report.model'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { array, bool, object, string } from 'yup'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { COUNTRIES_EN, FREQUENCIES, FREQUENCY, renderIdentifier } from '../../../util/Constants'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IAttachment } from '../../media/Media.model'
import { IReportAttachmentDto } from '../ReportAttachment.model'
import { FilePicker } from '../../../common/component/FilePicker'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { ContactEditor } from '../../contact/components/ContactEditor'
import { IContactDto } from '../../contact/Contact.model'
import ReportMetadataFormValidator from '../../report-metadata/ReportMetadataFormValidator.util'
import { DateTimeInputPicker } from '../../../common/component/DateTimeInputPicker'
import { IMetadata } from '../../report-metadata/ReportMetadata.model'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { AssetEmbedered } from '../../asset/components/AssetEmbedered'
import {get} from 'lodash'
import { TicketEmbedered } from '../../helpdesk/ticket/components/TicketEmbedered'
import { PriorityLevel, PriorityLevelOptions } from '../../helpdesk/ticket/Ticket.model'
import classNames from 'classnames'

interface IUpdateFormikSchema {
  id: string
  status: string
  serviceDesk: boolean
  startedAt: string
  endedAt: string
  countryCode?: string
  country?: string
  address: string
  latitude?: string
  longitude?: string
  visitType: string
  category: string
  frequencyCode: string
  frequency: string
  technicians: IContactDto[]
  contacts: IContactDto[]
  finalVersion: boolean
  metadata: IMetadata
  attachments: IAttachment[]
  workOrderRequest?: IReportWorkOrderRequestDto
}

export const ReportEditPage = () => {
  const intl = useIntl()
  const formValidator = new ReportMetadataFormValidator(intl);
  const navigate = useNavigate()
  const {reportId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.REPORTS'}),
      path: '/reports',
      isSeparator: false,
      isActive: false,
    },
  ]

  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [visitTypes, setVisitTypes] = useState(['CONTRATO', 'GARANTIA', 'PUNTUAL'])
  const [serviceCategories, setServiceCategories] = useState([
    'CORRECTIVO',
    'INSTALACIÓN',
    'MANTENIMIENTO PREVENTIVO',
    'MANTENIMIENTO MAYOR',
    'REVISIÓN Y DIAGNOSTIGO',
  ])
  const [validationSchema, setValidationSchema] = useState<any>(
    object().shape({
      id: formValidator.validateStringStrict(),
      status: formValidator.validateStringStrict(),
      startedAt: formValidator.validateInputDate(),
      endedAt: formValidator.validateInputDate(),
      countryCode: formValidator.validateString(),
      address: formValidator.validateStringStrict(),
      latitude: formValidator.validateString(),
      longitude: formValidator.validateString(),
      visitType: formValidator.validateStringStrict(),
      category: formValidator.validateStringStrict(),
      frequencyCode: formValidator.validateStringStrict(),
      frequency: string().when('frequencyCode', {
        is: (value) => value === FREQUENCY.OTHER,
        then: () => formValidator.validateStringStrict(),
      }),
      contacts: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      technicians: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      finalVersion: bool().required(),
      metadata: object().when('finalVersion', {
        is: true,
        then: () => formValidator.validateStrict(),
        otherwise: () => formValidator.validate(),
      }),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
      workOrderRequest: object().shape({
        lines: array()
          .of(
            object().shape({
              workOrderTypeCode: formValidator.validateString(),
              details: string().when('workOrderTypeCode', {
                is: (value) => !!value,
                then: () => formValidator.validateStringStrict(),
                otherwise: () => formValidator.validateString(),
              }),
            })
          )
          .when('attachments', {
            is: (attachments) => Array.isArray(attachments) && attachments.length > 0,
            then: (schema) =>
              schema
                .min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1}))
                .test(
                  'work-order-type-required-when-attachments',
                  intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}),
                  (lines) =>
                    Array.isArray(lines) && lines.every((line) => !!line?.workOrderTypeCode)
                ),
          }),
        priorityCode: string().when(['lines', 'attachments'], {
          is: (lines, attachments) =>
            (Array.isArray(attachments) && attachments.length > 0) ||
            (Array.isArray(lines) && lines.some((line) => !!line?.workOrderTypeCode)),
          then: () => formValidator.validateStringStrict(),
          otherwise: () => formValidator.validateString(),
        }),
      }),
    })
  )
  const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
    id: '',
    status: '',
    serviceDesk: false,
    startedAt: '',
    endedAt: '',
    countryCode: '',
    country: '',
    address: '',
    latitude: '',
    longitude: '',
    visitType: '',
    category: '',
    frequencyCode: '',
    frequency: '',
    contacts: [],
    technicians: [],
    finalVersion: true,
    metadata: {
      items: [],
    },
    attachments: [],
    workOrderRequest: {
      reportedBy: '',
      priorityCode: undefined,
      priority: undefined,
      lines: [],
      attachments: [],
    },
  })

  const didFetchReport = useRef(false)
  const [report, setReport] = useState<IReportDto | undefined>()
  const [isReportLoading, setIsReportLoading] = useState(true)
  const [reportGetById] = useReportGetByIdMutation()
  const [reportUpdateById] = useReportUpdateByIdMutation()

  const didFetchAttachments = useRef(false)
  const didAttachmentRequestDelete = useRef(false)
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false)
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)
  const [reportAttachmentSearch] = useReportAttachmentSearchMutation()
  const [reportAttachmentDeleteById] = useReportAttachmentDeleteByIdMutation()

  const didFetchTicketAttachments = useRef(false)
  const didTicketAttachmentRequestDelete = useRef(false)
  const [isTicketAttachmentsLoading, setIsTicketAttachmentsLoading] = useState(false)
  const [isTicketAttachmentProcessing, setIsTicketAttachmentProcessing] = useState(false)
  const [reportTicketAttachmentSearch] = useReportTicketAttachmentSearchMutation()
  const [reportTicketAttachmentDeleteById] = useReportTicketAttachmentDeleteByIdMutation()

  const formik = useFormik<IUpdateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IReportUpdateRequestDto = {
            id: values.id,
            status: values.finalVersion ? IReportStatus.COMPLETED : IReportStatus.PENDING,
            startedAt: values.startedAt ?? '',
            endedAt: values.endedAt ?? '',
            countryCode: values.countryCode,
            country: intl.formatMessage({id: `DROPDOWN.COUNTRY.${values.countryCode}`}),
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            visitType: values.visitType,
            category: values.category,
            frequencyCode: values.frequencyCode,
            frequency:
              values.frequencyCode === FREQUENCY.OTHER
                ? values.frequency.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.FREQUENCY.${values.frequencyCode}`}),
            contacts: values.contacts,
            technicians: values.technicians,
            metadata: values.metadata,
            attachments: values.attachments,
          }

          const workOrderRequest: IReportWorkOrderRequestDto | undefined = values.workOrderRequest
          if (workOrderRequest && workOrderRequest.lines && workOrderRequest.lines.length > 0) {
            workOrderRequest.lines = workOrderRequest.lines.map((line) => {
              return {
                ...line,
                workOrderType: intl.formatMessage({
                  id: `${`WORK_ORDER_LINE_TYPE.${line.workOrderTypeCode}`.toUpperCase()}`,
                }),
              }
            })
            const priority = intl.formatMessage({
              id: `TICKET.PRIORITY.${workOrderRequest.priorityCode || PriorityLevel.LOW}`,
            })
            workOrderRequest.priority = priority;
          }
          request.workOrderRequest = workOrderRequest;
         
          const response: any = await reportUpdateById(request).unwrap()
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/reports/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchReport = async (reportId) => {
      try {
        if (!didFetchReport.current) {
          setIsReportLoading(true)
          const response: any = await reportGetById({id: reportId})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const report: IReportDto = response.data
            setReport(report)

            formik.setFieldValue('id', report.id)
            formik.setFieldValue('status', report.status)
            formik.setFieldValue('serviceDesk', report.serviceDesk)
            formik.setFieldValue('startedAt', report.startedAt)
            formik.setFieldValue('endedAt', report.endedAt)
            formik.setFieldValue('countryCode', report.countryCode)
            formik.setFieldValue('address', report.address)
            formik.setFieldValue('latitude', report.latitude)
            formik.setFieldValue('longitude', report.longitude)
            formik.setFieldValue('visitType', report.visitType)
            formik.setFieldValue('category', report.category)
            formik.setFieldValue('frequencyCode', report.frequencyCode)
            formik.setFieldValue('frequency', report.frequency)
            formik.setFieldValue('contacts', report.contacts)
            formik.setFieldValue('technicians', report.technicians)
            formik.setFieldValue('finalVersion', report.status === IReportStatus.COMPLETED)
            formik.setFieldValue('metadata', report.metadata)
            formik.setFieldValue('ticketReferences', report.ticketReferences)
            formik.setFieldValue('workOrderRequest', report.workOrderRequest)

            await searchAttachment(report.id)
            await searchTicketAttachment(report.id)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsReportLoading(false)
      }
    }

    const searchAttachment = async (reportId) => {
      try {
        if (!didFetchAttachments.current) {
          setIsAttachmentsLoading(true)
          const response: any = await reportAttachmentSearch({
            filters: [{id: IReportFilter.HASH, value: reportId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const attachments: IReportAttachmentDto[] = response.data.data
            formik.setFieldValue(
              'attachments',
              attachments?.map((attachment) => {
                const file: IAttachment = {
                  id: attachment.id,
                  fileName: attachment.fileName,
                  temporalPath: attachment.temporalPath,
                  path: attachment.path,
                  size: attachment.size,
                  contentType: attachment.contentType,
                  comments: attachment.comments,
                  status: attachment.status,
                  processing: false,
                  uploaded: true,
                }
                return file
              })
            )
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAttachmentsLoading(false)
      }
    }

    const searchTicketAttachment = async (reportId) => {
      try {
        if (!didFetchAttachments.current) {
          setIsTicketAttachmentsLoading(true)
          const response: any = await reportTicketAttachmentSearch({
            filters: [{id: IReportFilter.HASH, value: reportId, editable: false}],
          })
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const attachments: IReportAttachmentDto[] = response.data.data
            formik.setFieldValue(
              'workOrderRequest.attachments',
              attachments?.map((attachment) => {
                const file: IAttachment = {
                  id: attachment.id,
                  fileName: attachment.fileName,
                  temporalPath: attachment.temporalPath,
                  path: attachment.path,
                  size: attachment.size,
                  contentType: attachment.contentType,
                  comments: attachment.comments,
                  status: attachment.status,
                  processing: false,
                  uploaded: true,
                }
                return file
              })
            )
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsTicketAttachmentsLoading(false)
      }
    }

    if (reportId) {
      fetchReport(reportId)
    }
  }, [])

  const deleteAttachmentOnClickHandler = async (id: string) => {
    try {
      if (id && !didAttachmentRequestDelete.current) {
        const response: any = await reportAttachmentDeleteById(id)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          formik.setFieldValue(
            'attachments',
            formik.values.attachments.filter((attachment) => attachment.id !== id)
          )
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    }
  }

  const deleteTicketAttachmentOnClickHandler = async (id: string) => {
    try {
      if (id && !didTicketAttachmentRequestDelete.current && formik.values.workOrderRequest) {
        const response: any = await reportTicketAttachmentDeleteById(id)
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          formik.setFieldValue(
            'workOrderRequest.attachments',
            formik.values.workOrderRequest.attachments.filter((attachment) => attachment.id !== id)
          )
        }
      }
    } catch (e) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    }
  }

  const removeDetail = (index) => {
    formik.setFieldValue(
      `workOrderRequest.lines`,
      get(formik.values, `workOrderRequest.lines`).filter((_, i) => i !== index)
    )
  }

  const addDetail = () => {
    const workOrderRequest: IReportWorkOrderRequestDto = get(formik.values, 'workOrderRequest')
    formik.setFieldValue('workOrderRequest.lines', [
      ...workOrderRequest.lines,
      {
        workOrderTypeCode: IWorkOrderLineType.SPARE_PART,
        workOrderType: IWorkOrderLineType.SPARE_PART,
        details: '',
      },
    ])
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.status && formik.errors.status) ||
          (formik.touched.startedAt && formik.errors.startedAt) ||
          (formik.touched.endedAt && formik.errors.endedAt) ||
          (formik.touched.countryCode && formik.errors.countryCode) ||
          (formik.touched.address && formik.errors.address) ||
          (formik.touched.latitude && formik.errors.latitude) ||
          (formik.touched.longitude && formik.errors.longitude) ||
          (formik.touched.visitType && formik.errors.visitType) ||
          (formik.touched.category && formik.errors.category) ||
          (formik.touched.frequencyCode && formik.errors.frequencyCode) ||
          (formik.touched.frequency && formik.errors.frequency)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.contacts && formik.errors.contacts
      }
      case 'kt_tab_pane_3': {
        return formik.touched.technicians && formik.errors.technicians
      }
      case 'kt_tab_pane_4': {
        return formik.touched.metadata && formik.errors.metadata
      }
      case 'kt_tab_pane_5': {
        return formik.touched.attachments && formik.errors.attachments
      }
      case 'kt_tab_pane_6': {
        const hasWorkOrderErrors = !!get(formik.errors, 'workOrderRequest')
        const workOrderTouched = !!get(formik.touched, 'workOrderRequest')
        return hasWorkOrderErrors && (workOrderTouched || formik.submitCount > 0)
      }
      default:
        return false
    }
  }
  
  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {report && (
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fs-2 fw-bolder'>
                            {`${new Date(Date.parse(report.createdAt)).toLocaleString()} - ${
                              report.client.displayName
                            } `}
                          </span>
                          <span className='fw-bolder text-primary me-1 fs-5'>
                            {renderIdentifier(report.sequence)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      {report && (
                        <>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary me-2 d-none d-md-block'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <>
                                <i className='bi bi-floppy'></i>
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'UTIL.SAVE'})}
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                            onClick={() => formik.handleSubmit()}
                            disabled={isAttachmentProcessing || formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                <span className='spinner-border spinner-border-sm align-middle'></span>
                              </span>
                            ) : (
                              <i className='bi bi-floppy'></i>
                            )}
                          </button>
                        </>
                      )}

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/reports/${reportId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isReportLoading && !formik.isSubmitting ? (
                <Spinner />
              ) : !report ? (
                <div className='row'>
                  <div className='d-flex flex-row-fluid flex-start mt-10'>
                    <span className='text-danger '>
                      <div className='d-flex flex-row justify-content-start align-items-center'>
                        <span className='fs-6 me-1'>
                          <i className='text-danger me-1 bi bi-exclamation-octagon' />
                          {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {tabWithError('kt_tab_pane_1') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                        {tabWithError('kt_tab_pane_2') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                        {tabWithError('kt_tab_pane_3') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.TECHNICIANS'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                        {tabWithError('kt_tab_pane_4') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.REPORT'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_5'>
                        {tabWithError('kt_tab_pane_5') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                      </a>
                    </li>
                    {/* HIDDEN SPARE PARTS FEATURE TAB FOR NOW */}
                    {/* {report.workOrderRequest &&
                      (!report.ticketReferences || report.ticketReferences.length === 0) && (
                        <li className='nav-item'>
                          <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_6'>
                            {intl.formatMessage({id: 'UTIL.SPARE_PARTS'})}
                          </a>
                        </li>
                      )} */}
                  </ul>
                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <ClientEmbedered client={report.client} />
                          {report.asset && <AssetEmbedered asset={report.asset} />}
                        </div>
                      </div>

                      {report.ticketReferences && report.ticketReferences.length > 0 && (
                        <div className='row mb-5'>
                          <SubSectionHeader
                            separator={false}
                            title={intl.formatMessage({
                              id: 'REPORT.WORK_ORDER_REQUEST.COMPLETE.LABEL',
                            })}
                          />
                          <div className='px-5'>
                            <div className='row mb-5'>
                              {report.ticketReferences.map((ticket) => (
                                <TicketEmbedered key={ticket.id} ticketId={ticket.id!} />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SERVICE_DESK'})}
                              disabled={formik.isSubmitting}
                              object={
                                <div className='form-check form-switch form-check-custom form-check-solid ps-3'>
                                  <input
                                    id='draftSwitchChecked'
                                    className='form-check-input h-25px w-35px'
                                    type='checkbox'
                                    {...formik.getFieldProps(`serviceDesk`)}
                                    onChange={formik.handleChange}
                                    checked={formik.values.serviceDesk}
                                    disabled={formik.isSubmitting}
                                  />
                                </div>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.FINAL_VERSION'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='status'
                              object={
                                <div className='form-check form-switch form-check-custom form-check-solid ps-3'>
                                  <input
                                    id='draftSwitchChecked'
                                    className='form-check-input h-25px w-35px'
                                    type='checkbox'
                                    {...formik.getFieldProps(`finalVersion`)}
                                    onChange={formik.handleChange}
                                    checked={formik.values.finalVersion}
                                    disabled={
                                      formik.values.status === IReportStatus.COMPLETED ||
                                      formik.isSubmitting
                                    }
                                  />
                                </div>
                              }
                            ></CustomInputRenderer>

                            <DateTimeInputPicker
                              label={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                              placeholder={intl.formatMessage({id: 'UTIL.DATE_TIME_START'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='startedAt'
                              selectOnClickHandler={(date) => {
                                if (formik.values.endedAt) {
                                  const endedAt = new Date(formik.values.endedAt)
                                  if (date > endedAt) {
                                    formik.setFieldValue('endedAt', '')
                                  }
                                }
                              }}
                            ></DateTimeInputPicker>

                            <DateTimeInputPicker
                              label={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                              placeholder={intl.formatMessage({id: 'UTIL.DATE_TIME_END'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='endedAt'
                              minDateEnable={!!formik.values.startedAt}
                              minDate={((): string | undefined => {
                                if (formik.values.startedAt) {
                                  const startedAt = new Date(formik.values.startedAt)
                                  return new Date(
                                    startedAt.getFullYear(),
                                    startedAt.getMonth(),
                                    startedAt.getDate(),
                                    startedAt.getHours(),
                                    startedAt.getMinutes() + 1,
                                    0
                                  ).toUTCString()
                                }
                                return undefined
                              })()}
                              selectOnClickHandler={(date) => {
                                if (formik.values.startedAt) {
                                  const startedAt = new Date(formik.values.startedAt)
                                  if (startedAt > date) {
                                    formik.setFieldValue('endedAt', '')
                                  }
                                }
                              }}
                            ></DateTimeInputPicker>

                            {report.contract ? (
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                                value={`${report.visitType}`}
                              ></CustomInputRenderer>
                            ) : (
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.VISIT_TYPE'})}
                                required={true}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName='visitType'
                                object={
                                  <select
                                    className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                    {...formik.getFieldProps('visitType')}
                                    onChange={(e) => formik.handleChange(e)}
                                    disabled={formik.isSubmitting}
                                  >
                                    <option value=''>
                                      {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                    </option>
                                    {visitTypes &&
                                      visitTypes.map((visitType, index) => (
                                        <option key={index} value={visitType}>
                                          {visitType}
                                        </option>
                                      ))}
                                  </select>
                                }
                              ></CustomInputRenderer>
                            )}

                            {report.contract ? (
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                                value={`${
                                  report.frequencyCode === FREQUENCY.OTHER
                                    ? report.frequency
                                    : intl.formatMessage({
                                        id: `DROPDOWN.FREQUENCY.${report.frequencyCode}`,
                                      })
                                }`}
                              ></CustomInputRenderer>
                            ) : (
                              <>
                                <CustomInputRenderer
                                  label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                                  required={true}
                                  disabled={formik.isSubmitting}
                                  formik={formik}
                                  fieldName='frequencyCode'
                                  object={
                                    <select
                                      className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                      {...formik.getFieldProps('frequencyCode')}
                                      onChange={(e) => {
                                        if (e.target.value === FREQUENCY.OTHER) {
                                          formik.setFieldValue('frequency', '')
                                        }
                                        formik.handleChange(e)
                                      }}
                                      disabled={formik.isSubmitting}
                                    >
                                      <option value=''>
                                        {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                      </option>
                                      {FREQUENCIES.map((frequency, index) => (
                                        <option key={index} value={frequency}>
                                          {intl.formatMessage({
                                            id: `DROPDOWN.FREQUENCY.${frequency}`,
                                          })}
                                        </option>
                                      ))}
                                    </select>
                                  }
                                ></CustomInputRenderer>

                                {formik.values.frequencyCode === FREQUENCY.OTHER && (
                                  <CustomInputRenderer
                                    placeholder={intl.formatMessage({id: 'UTIL.SPECIFY'})}
                                    disabled={formik.isSubmitting}
                                    formik={formik}
                                    fieldName={`frequency`}
                                    enableInputLength={true}
                                  ></CustomInputRenderer>
                                )}
                              </>
                            )}

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.CATEGORY'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='category'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('category')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {serviceCategories &&
                                    serviceCategories.map((category, index) => (
                                      <option key={index} value={category}>
                                        {category}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='countryCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('countryCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {COUNTRIES_EN &&
                                    COUNTRIES_EN.map((country, index) => (
                                      <option key={index} value={country.code}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.COUNTRY.${country.code}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              disabled={formik.isSubmitting}
                              required={true}
                              formik={formik}
                              fieldName='address'
                              enableInputLength={true}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor
                              formik={formik}
                              fieldName='contacts'
                              disabled={formik.isSubmitting}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor
                              formik={formik}
                              fieldName='technicians'
                              disabled={formik.isSubmitting}
                              primaryLabel={intl.formatMessage({id: 'UTIL.PRIMARY_TECHNICIAN'})}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                      <div className='row mb-5'>
                        {report && report.metadata && (
                          <ReportRenderer
                            formik={formik}
                            path={`metadata.items`}
                            parameters={{FINAL_VERSION: get(formik.values, `finalVersion`)}}
                          />
                        )}
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_5' role='tabpanel'>
                      <div className='row mb-5'>
                        <FilePicker
                          disabled={formik.isSubmitting}
                          formik={formik}
                          fieldName='attachments'
                          loading={isAttachmentsLoading}
                          deleteConfirmationModalId='report-edit-attachments-delete-confirmation-modal'
                          fileDeleteOnClickHandler={deleteAttachmentOnClickHandler}
                          setIsAttachmentProcessing={setIsAttachmentProcessing}
                        />
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_6' role='tabpanel'>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({
                            id: 'REPORT.WORK_ORDER_REQUEST.DETAILS.HEADER',
                          })}
                        />

                        {!get(formik.values, `workOrderRequest.lines`) ||
                          (get(formik.values, `workOrderRequest.lines`).length === 0 && (
                            <CustomInputRenderer
                              object={
                                <span className='fs-8 text-danger'>
                                  {intl.formatMessage({
                                    id: 'REPORT.WORK_ORDER_REQUEST.DETAILS.MESSAGE.LABEL',
                                  })}
                                </span>
                              }
                            ></CustomInputRenderer>
                          ))}

                        <FieldArray name='details'>
                          {() => (
                            <>
                              {get(formik.values, `workOrderRequest.lines`) &&
                                get(formik.values, `workOrderRequest.lines`).map((line, index) => (
                                  <div className='row mx-0 px-0' key={index}>
                                    <div className='col-xxl-3 my-2'>
                                      <div className='d-flex justify-content-end align-items-center'>
                                        <span className='text-white'>
                                          <span
                                            className='border badge bg-light-secondary cursor-pointer'
                                            style={{zIndex: '10'}}
                                            title={intl.formatMessage({id: 'UTIL.REMOVE'})}
                                            onClick={() =>
                                              !formik.isSubmitting && removeDetail(index)
                                            }
                                          >
                                            <div className='d-flex flex-row justify-content-start align-items-center'>
                                              <i
                                                className={classNames({
                                                  'bi bi-x fs-2 text-danger': !formik.isSubmitting,
                                                  'bi bi-x fs-2 text-secondary':
                                                    formik.isSubmitting,
                                                })}
                                              />
                                            </div>
                                          </span>
                                        </span>
                                      </div>
                                    </div>

                                    <div className='col-xxl-9'>
                                      <div className='row'>
                                        <div className='col-xxl-4 my-1'>
                                          <select
                                            className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                            {...formik.getFieldProps(
                                              `workOrderRequest.lines.${index}.workOrderTypeCode`
                                            )}
                                            onChange={(e) => formik.handleChange(e)}
                                            disabled={formik.isSubmitting}
                                          >
                                            <option value=''>
                                              {intl.formatMessage({
                                                id: 'UTIL.DROPDOWN.DEFAULT',
                                              })}
                                            </option>
                                            <option value={IWorkOrderLineType.SPARE_PART}>
                                              {intl.formatMessage({
                                                id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.SPARE_PART}`}`,
                                              })}
                                            </option>
                                            <option value={IWorkOrderLineType.MAINTENANCE}>
                                              {intl.formatMessage({
                                                id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.MAINTENANCE}`}`,
                                              })}
                                            </option>
                                            <option value={IWorkOrderLineType.OTHER}>
                                              {intl.formatMessage({
                                                id: `${`WORK_ORDER_LINE_TYPE.${IWorkOrderLineType.OTHER}`}`,
                                              })}
                                            </option>
                                          </select>
                                          <div className='d-flex flex-row p-1'>
                                            <div className='d-flex flex-column flex-row-fluid'>
                                              <div className='d-flex flex-row flex-column-fluid'>
                                                <div className='d-flex flex-row-fluid flex-start'>
                                                  {get(
                                                    formik.touched,
                                                    `workOrderRequest.lines.${index}.details`
                                                  ) &&
                                                    get(
                                                      formik.errors,
                                                      `workOrderRequest.lines.${index}.details`
                                                    ) && (
                                                      <span className='fs-8 text-danger'>
                                                        {get(
                                                          formik.errors,
                                                          `workOrderRequest.lines.${index}.details`
                                                        )?.toUpperCase() ?? ''}
                                                      </span>
                                                    )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className='col-xxl-8 my-1'>
                                          <input
                                            type='text'
                                            className='form-control form-control-sm form-control-solid px-5 py-0 fs-6 text-dark'
                                            placeholder={intl.formatMessage({
                                              id: 'UTIL.DETAILS',
                                            })}
                                            {...formik.getFieldProps(
                                              `workOrderRequest.lines.${index}.details`
                                            )}
                                            onChange={(e) =>
                                              formik.setFieldValue(
                                                `workOrderRequest.lines.${index}.details`,
                                                e.target.value.toUpperCase()
                                              )
                                            }
                                            disabled={formik.isSubmitting}
                                          />
                                          <div className='d-flex flex-row p-1'>
                                            <div className='d-flex flex-column flex-row-fluid'>
                                              <div className='d-flex flex-row flex-column-fluid'>
                                                <div className='d-flex flex-row-fluid flex-start'>
                                                  {get(
                                                    formik.touched,
                                                    `workOrderRequest.lines.${index}.details`
                                                  ) &&
                                                    get(
                                                      formik.errors,
                                                      `workOrderRequest.lines.${index}.details`
                                                    ) && (
                                                      <span className='fs-8 text-danger'>
                                                        {get(
                                                          formik.errors,
                                                          `workOrderRequest.lines.${index}.details`
                                                        )?.toUpperCase() ?? ''}
                                                      </span>
                                                    )}
                                                </div>

                                                {formik &&
                                                  get(
                                                    formik.values,
                                                    `workOrderRequest.lines.${index}.details`
                                                  )?.length > 0 && (
                                                    <div className='d-flex flex-row-auto flex-end align-items-start'>
                                                      <span className='fs-8 text-muted'>{`${
                                                        get(
                                                          formik.values,
                                                          `workOrderRequest.lines.${index}.details`
                                                        )?.length || 0
                                                      }/255`}</span>
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              <div className='row mx-0 px-0 mt-2'>
                                <div className='col-xxl-3'>
                                  <div className='d-flex justify-content-end align-items-center mb-3'>
                                    <span
                                      className='border badge bg-light-secondary cursor-pointer'
                                      style={{zIndex: '10'}}
                                      title={intl.formatMessage({id: 'UTIL.ADD'})}
                                      onClick={() => !formik.isSubmitting && addDetail()}
                                    >
                                      <div className='d-flex flex-row justify-content-start align-items-center'>
                                        <i
                                          className={classNames({
                                            'bi bi-plus fs-2 text-success': !formik.isSubmitting,
                                            'bi bi-plus fs-2 text-secondary': formik.isSubmitting,
                                          })}
                                        />
                                      </div>
                                    </span>
                                  </div>
                                </div>
                                <div className='col-xxl-9'></div>
                              </div>
                            </>
                          )}
                        </FieldArray>
                      </div>

                      <div className='row mb-5'>
                        <CustomInputRenderer
                          label={intl.formatMessage({id: 'UTIL.PRIORITY_LEVEL'})}
                          disabled={formik.isSubmitting}
                          formik={formik}
                          fieldName='workOrderRequest.priorityCode'
                          object={
                            <select
                              className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                              {...formik.getFieldProps('workOrderRequest.priorityCode')}
                              onChange={(e) => formik.handleChange(e)}
                              disabled={formik.isSubmitting}
                              title={intl.formatMessage({
                                id: 'TICKET.IMPACT_AND_PRIORITY.REQUIRED',
                              })}
                            >
                              <option value=''>
                                {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                              </option>
                              {PriorityLevelOptions &&
                                PriorityLevelOptions.map((priorityLevel, index) => (
                                  <option key={index} value={priorityLevel}>
                                    {intl.formatMessage({id: `TICKET.PRIORITY.${priorityLevel}`})}
                                  </option>
                                ))}
                            </select>
                          }
                        ></CustomInputRenderer>
                        <CustomInputRenderer
                          label={intl.formatMessage({id: 'REPORT.WORK_ORDER_REQUEST.NOTES.LABEL'})}
                          disabled={formik.isSubmitting}
                          formik={formik}
                          fieldName='workOrderRequest.observations'
                          object={
                            <textarea
                              rows={4}
                              className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                              placeholder={intl.formatMessage({
                                id: 'REPORT.WORK_ORDER_REQUEST.NOTES.PLACEHOLDER',
                              })}
                              {...formik.getFieldProps('workOrderRequest.observations')}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  'workOrderRequest.observations',
                                  e.target.value.toUpperCase()
                                )
                              }
                              disabled={formik.isSubmitting}
                            />
                          }
                          enableInputLength={true}
                          inputLength={3000}
                        ></CustomInputRenderer>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({
                            id: 'UTIL.ATTACHMENTS',
                          })}
                        />
                        <FilePicker
                          disabled={formik.isSubmitting}
                          formik={formik}
                          fieldName='workOrderRequest.attachments'
                          loading={isTicketAttachmentsLoading}
                          deleteConfirmationModalId='report-edit-work-order-attachments-delete-confirmation-modal'
                          fileDeleteOnClickHandler={deleteTicketAttachmentOnClickHandler}
                          setIsAttachmentProcessing={setIsTicketAttachmentProcessing}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}
